import React, {Suspense, lazy, useContext, useEffect} from 'react';
import {AppRoot} from '@tkww/orion-web-app-kernel';
import {Switch, Route, Redirect} from 'react-router-dom';
import track from 'react-tracking';
import {noop} from 'lodash';
import AppLayout from './components/AppLayout';
import MemberContext from './contexts/MemberContext';
import YourRegistryHeader from './components/YourRegistryHeader';
import ScrollToTop from './components/ScrollToTop';
import FullScreenSpinner from './components/FullScreenSpinner';
import useIsMobileApp from './hooks/useIsMobileApp';
import trackSegmentData from './utils/trackSegmentData';

const Cash = lazy(() => import('./views/Cash'));
const CashBankDetails = lazy(() => import('./views/CashBankDetails'));
const CashTransfer = lazy(() => import('./views/CashTransfer'));
const Checklist = lazy(() => import('./views/Checklist'));
const ManageRegistry = lazy(() => import('./views/ManageRegistry'));
const Onboarding = lazy(() => import('./views/Onboarding'));
const Settings = lazy(() => import('./views/Settings'));
const Shop = lazy(() => import('./views/Shop'));
const Stores = lazy(() => import('./views/Stores'));
const TrackGifts = lazy(() => import('./views/TrackGifts'));

const NoMatchRedirect = () => <Redirect to="/shop" />;

export const Routes = () => {
  const {member, isFetching: isFetchingMember} = useContext(MemberContext);
  const isMobileApp = useIsMobileApp();

  /**
   * Once the app mounts in development mode, we ensure that the
   * ttq object is mocked to prevent the app from crashing when
   * tracking events are sent.
   */
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    window.ttq = window.ttq || {
      track: () => noop,
    };
  }, []);

  const renderProtectedTransferRoute = Component => routeProps => {
    if (isFetchingMember) {
      return <FullScreenSpinner />;
    }

    if (!member?.id || !isMobileApp) {
      return <NoMatchRedirect />;
    }

    return <Component {...routeProps} />;
  };

  return (
    <>
      <ScrollToTop />
      <Route
        component={YourRegistryHeader}
        path={[
          '/shop',
          '/checklist',
          '/manage-registry',
          '/track-gifts',
          '/settings',
        ]}
      />
      <Suspense fallback={<FullScreenSpinner />}>
        <Switch>
          <Route component={Stores} exact path="/add-store-registries" />
          <Route component={Cash} exact path="/cash" />
          <Route
            exact
            path="/cash/transfer"
            render={renderProtectedTransferRoute(CashTransfer)}
          />
          <Route
            exact
            path="/cash/transfer/bank-details"
            render={renderProtectedTransferRoute(CashBankDetails)}
          />
          <Route component={Shop} exact path="/shop" />
          {/* Old Overview and Discover URLs live on in bookmarks and other repos */}
          <Route
            path={['/overview', '/discover']}
            render={({location}) => (
              <Redirect to={{pathname: '/shop', search: location.search}} />
            )}
          />
          <Route component={ManageRegistry} exact path="/manage-registry" />
          <Route component={TrackGifts} exact path="/track-gifts" />
          <Route
            component={Settings}
            exact
            path="/settings/:option(registry-url|registry-note|shipping-address|cash-funds|cash-funds/account-settings)"
          />
          <Redirect from="/settings" to="/settings/registry-url" />
          <Route component={Checklist} exact path="/checklist" />
          <Route component={Checklist} path="/checklist/:sectionId" />
          <Route component={Onboarding} path="/onboarding" />
          <Route component={NoMatchRedirect} />
        </Switch>
      </Suspense>
    </>
  );
};

const App = () => {
  return (
    <AppRoot>
      <AppLayout>
        <Routes />
      </AppLayout>
    </AppRoot>
  );
};

export default track(
  {},
  {
    dispatch: data => trackSegmentData(data),
  },
)(App);
